import React, { useState, useMemo, useEffect, useCallback } from "react";

import { API_BASE_URL, endpoint } from "../../api/endpoint";
import { useResponsiveItemsPerPage } from "../../components/Constants/Constants";
import { useToasts } from "react-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import { apiHandler } from "../../api";

export const useOrders = () => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();

  const { authData,storeId } = useSelector((state) => state.loginSlice);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search states
  const [selectedOption, setSelectedOption] = useState("buyer_order_id");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  // Filter states (including static and dynamic)
  const [filters, setFilters] = useState({
    order_created_at_from: "",
    order_created_at_to: "",
    order_status: "",
  });

  // Dynamic filter options fetched from the API
  const [filterOptions, setFilterOptions] = useState({
    buyerAppIds: [],
    sellerAppIds: [],
    orderStatuses: [],
    settlementStatuses: [],
  });

  const searchOptions = [
    { value: "buyer_order_id", label: "Buyer Order ID" },
    { value: "txn_id", label: "Transaction ID" },
    { value: "seller_order_id", label: "Seller Order ID" },
    // Add other options you need here
  ];

  useEffect(() => {
    // Debounce the search input
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);


  const getOrders = useCallback(async () => {
  try {
    setLoading(true);

    let url = `${endpoint.GET_ORDERS}`;

    // Search parameters
    const params = new URLSearchParams();

    if (debouncedSearchValue) {
      params.append(selectedOption, debouncedSearchValue);
    }

    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        let value = filters[key];

        if (key === "order_created_at_to") {
          const toDate = new Date(value);
          toDate.setHours(23, 59, 59, 999);
          value = toDate.toISOString();
        }

        if (key === "order_created_at_from") {
          const fromDate = new Date(value);
          fromDate.setHours(0, 0, 0, 0);
          value = fromDate.toISOString();
        }

        params.append(key, value);
      }
    });

    // Sorting parameters
    if (sortConfig.key) {
      params.append("sort_by", sortConfig.key);
      params.append("sort_order", sortConfig.direction);
    }

    // Pagination parameters
    params.append("page", currentPage);
    params.append("limit", itemsPerPage);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const result = await apiHandler({
      url: url,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      authToken: authData,
      dispatch,
      storeId
    });

    if (result?.data?.data?.orders) {
      setOrders(result.data.data.orders);
      setTotalOrderCount(result.data.data.pagination?.count || 0);
    } else {
      setOrders([]);
      setTotalOrderCount(0);
     
    }

    if (result?.data?.data?.filter) {
      setFilterOptions({
        buyerAppIds: result.data.data.filter.buyer_app_ids || [],
        sellerAppIds: result.data.data.filter.seller_app_ids || [],
        domains: result.data.data.filter.domains || [],
        orderStatuses: result.data.data.filter.order_statuses || [],
        settlementStatuses: result.data.data.filter.settlement_statuses || [],
      });
    }
  } catch (error) {
   
  } finally {
    setLoading(false);
  }
}, [
  debouncedSearchValue,
  selectedOption,
  filters,
  sortConfig,
  currentPage,
  itemsPerPage,
]);

  useEffect(() => {
    getOrders();
  }, [getOrders]);

  // --- Handlers for UI Interactions ---
  const handleDropdownChange = (e) => {
    const selectedFilter = e.target.value;
    setSelectedOption(selectedFilter);
    setSearchValue("");
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    setCurrentPage(1);
  };

  const allowedSortKeys = [
    "order_amount",
    "finder_fee_amount",
    "order_created_at",
  ];
  const handleSort = (key) => {
    if (!allowedSortKeys.includes(key)) return; // Prevent sorting on other columns
    setSortConfig((current) => {
      const newConfig = {
        key,
        direction:
          current.key === key && current.direction === "asc" ? "desc" : "asc",
      };
      return newConfig;
    });
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };
      return newFilters;
    });
    setCurrentPage(1); // Reset page to 1 on filter change
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      order_status: "",
      order_created_at_from: "",
      order_created_at_to: "",
      settlement_status: "",
      domain: "",
      buyer_app_id: "",
      seller_app_id: "",
    });
    setSearchValue("");
    setSelectedOption("buyer_order_id");
    setSortConfig({ key: null, direction: "asc" });
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  };

  // --- Computed Values & Utility Functions ---
  const totalPages = Math.ceil(totalOrderCount / itemsPerPage);
  const startItem =
    totalOrderCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalOrderCount);

  // These are redundant if API handles sorting and pagination directly
  const paginatedOrders = useMemo(() => orders, [orders]);

  const handleCheckboxToggle = (id) => {
    setSelectedOrders((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Toggle "Select All"
  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedOrders([]);
    } else {
      const allIds = paginatedOrders.map((order) => order.id);
      setSelectedOrders(allIds);
    }
    setAllSelected(!allSelected);
  };

  const sendSettlementRequest = useCallback(
    async (e) => {
      e.preventDefault();

      if (selectedOrders.length === 0) return;

      try {
        setLoading(true);

        const url = `${endpoint.SEND_SETTLEMENT_REQUEST}`;
        const selectedOrder = orders.find(
          (order) => order.id === selectedOrders[0]
        );

        const payload = {
          order_ids: selectedOrders,
          app_details: {
            buyer_app_id: selectedOrder?.buyer_app_id,
            seller_app_id: selectedOrder?.seller_app_id,
            buyer_app_uri: selectedOrder?.buyer_app_uri,
            seller_app_uri: selectedOrder?.seller_app_uri,
          },
        };


        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId
        });

        const responseStatus = response?.data?.status || response?.status;
        const failedOrdersList =
          response?.data?.response?.failedOrders?.filter(
            (item) => item && typeof item === "object" && item.orderId
          ) || [];

        // Construct the base toast message
        let toastMessage =
          response.data.message || "Settlement request sent successfully";

        let failedOrdersContent = null;
        if (failedOrdersList.length > 0) {
          failedOrdersContent = (
            <div style={{ marginTop: "10px" }}>
              <strong>Failed Orders:</strong>
              <ul style={{ marginTop: "5px", color: "#e74c3c" }}>
                {failedOrdersList.map((order, index) => (
                  <li key={index}>
                    <strong>Order ID:</strong> {order.orderId} <br />
                    <strong>Error:</strong> {order.error}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        addToast(
          <>
            <div>{toastMessage}</div>
            {failedOrdersContent}
          </>,
          {
            appearance: responseStatus === 200 ? "success" : "error",
            autoDismiss: true,
          }
        );
      } catch (error) {

        if (error.response && error.response.data) {
          addToast(error.response.data.message || "Server error occurred", {
            appearance: "error",
            autoDismiss: true,
          });
        } else {
          addToast("Network error or unexpected issue", {
            appearance: "error",
            autoDismiss: true,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [selectedOrders, orders, authData, dispatch]
  );

  return {
    orders,
    loading,
    totalOrderCount,
    filters,
    setFilters,
    handleFilterChange,
    clearFilters,
    sortConfig,
    handleSort,
    paginatedOrders,
    totalPages,
    currentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    setItemsPerPage,
    handleItemsPerPageChange,
    filterOptions, // Include dynamic filters
    selectedOption,
    setSelectedOption,
    searchValue,
    setSearchValue,
    handleDropdownChange,
    handleSearchChange,
    selectedOrders,
    setSelectedOrders,
    handleCheckboxToggle,
    allSelected,
    handleSelectAll,
    searchOptions,
    sendSettlementRequest,
  };
};
